import React, { useState } from 'react';
import { useSchool } from '../context/SchoolContext';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, X, ChevronRight, Upload, FileDown, User, MapPin, CreditCard, BookOpen, LayoutGrid, List, Check, Save } from 'lucide-react';
import ImportModal from '../components/ImportModal';
import './Customers.css';

const Customers = () => {
    const { customers, loadMoreCustomers, hasMoreCustomers, totalCustomers, addCustomer, updateCustomer, packages, branches, installmentTypes, getPackageName, getInstallmentName, importCustomers, getBranchName, stages, sources, kpiStatuses, promotions = [] } = useSchool();
    const [isAddMode, setIsAddMode] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isTableView, setIsTableView] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [savedId, setSavedId] = useState(null);

    const customerTemplateHeaders = ['First Name', 'Surname', 'Phone', 'Email', 'ID Number', 'Gender', 'Package', 'Branch', 'Initial Payment', 'Current Stage', 'Balance', 'Address'];
    const customerSampleRow = ['John', 'Doe', '0771234567', 'john@example.com', '63-123456-A-63', 'Male', 'Full Package', 'Harare', '50', 'New', '300', '123 Main St, Harare'];

    const handleDownloadTemplate = () => {
        const csvContent = "data:text/csv;charset=utf-8,"
            + customerTemplateHeaders.join(",") + "\n"
            + customerSampleRow.join(",");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "customers_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // New Customer State
    const [newCustomer, setNewCustomer] = useState({
        firstName: '',
        surname: '',
        gender: 'Male',
        idNumber: '',
        phone: '',
        email: '',
        branch: branches[0]?.branch_name || '',
        packageId: packages[0]?.id,
        paymentPlan: installmentTypes[0]?.id,
        source: sources[0],
        stage: stages[0],
        kpiStatus: kpiStatuses[0],
        nextOfKin: { name: '', relationship: '', phone: '' },
        initialPaymentAmount: 0,
        initialPaymentDate: new Date().toISOString().split('T')[0],
        notes: '',
        address: ''
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('nok_')) {
            const nokField = name.replace('nok_', '');
            setNewCustomer(prev => ({
                ...prev,
                nextOfKin: { ...prev.nextOfKin, [nokField]: value }
            }));
        } else {
            setNewCustomer(prev => ({ ...prev, [name]: value }));
        }
    };

    const getActivePromotion = (pkgId) => {
        if (!pkgId || !promotions || promotions.length === 0) return null;
        const today = new Date().toISOString().split('T')[0];
        return promotions.find(promo =>
            String(promo.packageId) === String(pkgId) &&
            promo.startDate <= today &&
            promo.endDate >= today
        );
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const selectedPkg = packages.find(p => p.id === newCustomer.packageId);
        let totalAmount = selectedPkg ? selectedPkg.price : 0;

        const activePromo = getActivePromotion(newCustomer.packageId);
        if (activePromo) {
            if (activePromo.discountType && activePromo.discountType.toLowerCase() === 'percentage') {
                totalAmount = totalAmount - (totalAmount * (activePromo.discountValue / 100));
            } else if (activePromo.discountType && activePromo.discountType.toLowerCase() === 'flat') {
                totalAmount = Math.max(0, totalAmount - activePromo.discountValue);
            }
        }

        addCustomer({
            ...newCustomer,
            totalAmount,
            paidAmount: parseFloat(newCustomer.initialPaymentAmount) || 0,
            balance: totalAmount - (parseFloat(newCustomer.initialPaymentAmount) || 0),
            status: 'Active'
        });
        setIsAddMode(false);
        // Reset form
        setNewCustomer({
            firstName: '', surname: '', gender: 'Male', idNumber: '', phone: '', email: '',
            branch: branches[0]?.branch_name || '', packageId: packages[0]?.id, paymentPlan: installmentTypes[0]?.id,
            source: sources[0], stage: stages[0], kpiStatus: kpiStatuses[0],
            nextOfKin: { name: '', relationship: '', phone: '' },
            initialPaymentAmount: 0,
            initialPaymentDate: new Date().toISOString().split('T')[0],
            notes: '',
            address: ''
        });
    };


    const filteredCustomers = (customers || []).filter(c => {
        const name = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
        const phone = c.phone || c.phone_number || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            phone.includes(searchTerm);
    });

    const handleQuickUpdate = async (customer, field, value) => {
        setSavingId(`${customer.id}-${field}`);

        // Prepare data for updateCustomer (maintaining current state)
        const updatedData = {
            firstName: customer.first_name,
            surname: customer.last_name,
            idNumber: customer.national_id__no,
            gender: customer.gender,
            email: customer.email_address,
            phone: customer.phone_number,
            packageId: customer.package_id,
            branchId: customer.branch_id,
            stage: customer.stage,
            balance: customer.balance,
            address: customer.address,
            notes: customer.notes,
            ...customer, // Keep other fields
            [field === 'branch' ? 'branchId' : field === 'package' ? 'packageId' : field]: value
        };

        try {
            await updateCustomer(customer.id, updatedData);
            setSavedId(`${customer.id}-${field}`);
            setTimeout(() => setSavedId(null), 2000);
        } catch (error) {
            console.error('Failed to update customer:', error);
        } finally {
            setSavingId(null);
        }
    };


    return (
        <div className="page-container">
            <div className="page-header">
                <div>
                    <h2 className="page-title">Customers</h2>
                    <p className="page-subtitle">Manage enrolled students and their accounts</p>
                </div>
                {!isAddMode && (
                    <div className="flex gap-2">
                        <button
                            className="btn btn-secondary flex items-center gap-2"
                            onClick={handleDownloadTemplate}
                            title="Download CSV Template"
                        >
                            <FileDown size={18} /> Template
                        </button>
                        <button className="btn btn-secondary flex items-center gap-2" onClick={() => setIsImportModalOpen(true)}>
                            <Upload size={18} /> Import CSV
                        </button>
                        <button className="btn btn-primary flex items-center gap-2" onClick={() => setIsAddMode(true)}>
                            <Plus size={18} /> Add New Customer
                        </button>
                    </div>
                )}
            </div>

            {isAddMode ? (
                <div className="card add-customer-form-container fade-in">
                    <div className="form-header">
                        <div className="flex items-center gap-3">
                            <div className="icon-badge-gold">
                                <Plus size={20} />
                            </div>
                            <h3>Add New Customer</h3>
                        </div>
                        <button className="f-close-btn" style={{ background: '#f1f5f9' }} onClick={() => setIsAddMode(false)}><X size={20} /></button>
                    </div>

                    <form onSubmit={handleSubmit} className="premium-form">
                        {/* PERSONAL DETAILS */}
                        <div className="form-section">
                            <h4 className="section-title">PERSONAL DETAILS</h4>
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label>FIRST NAME</label>
                                    <input type="text" name="firstName" placeholder="e.g. John" required value={newCustomer.firstName} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>SURNAME</label>
                                    <input type="text" name="surname" placeholder="e.g. Doe" required value={newCustomer.surname} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>GENDER</label>
                                    <select name="gender" value={newCustomer.gender} onChange={handleInputChange}>
                                        <option value="Male">Male</option>
                                        <option value="Female">Female</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>ID NUMBER</label>
                                    <input type="text" name="idNumber" placeholder="63-XXXXXX-X-63" required value={newCustomer.idNumber} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>PHONE NUMBER</label>
                                    <input type="tel" name="phone" placeholder="+263 77..." required value={newCustomer.phone} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>BRANCH</label>
                                    <select name="branch" value={newCustomer.branch} onChange={handleInputChange}>
                                        {branches.map(b => <option key={b.id} value={b.branch_name}>{b.branch_name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group span-2">
                                    <label>ADDRESS</label>
                                    <input type="text" name="address" placeholder="Residential Address" value={newCustomer.address} onChange={handleInputChange} />
                                </div>
                            </div>
                        </div>

                        {/* TRAINING DETAILS */}
                        <div className="form-section">
                            <h4 className="section-title">TRAINING DETAILS</h4>
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label>PACKAGE</label>
                                    <select name="packageId" value={newCustomer.packageId} onChange={handleInputChange}>
                                        {packages.map(p => <option key={p.id} value={p.id}>{p.name} (${p.price})</option>)}
                                    </select>
                                    {(() => {
                                        const activePromo = getActivePromotion(newCustomer.packageId);
                                        if (activePromo) {
                                            const saved = activePromo.discountType === 'Percentage'
                                                ? `${activePromo.discountValue}%`
                                                : `$${activePromo.discountValue}`;
                                            return (
                                                <div className="mt-2 p-2 rounded bg-indigo-50 text-indigo-700 text-sm flex items-center gap-2 border border-indigo-100">
                                                    <span className="font-semibold px-2 py-0.5 bg-indigo-100 rounded text-xs text-indigo-800 shrink-0">PROMO APPLIED</span>
                                                    <span>{activePromo.name} <strong>({saved} off)</strong></span>
                                                </div>
                                            );
                                        }
                                        return null;
                                    })()}
                                </div>
                                <div className="form-group">
                                    <label>HEARD ABOUT US FROM</label>
                                    <select name="source" value={newCustomer.source} onChange={handleInputChange}>
                                        {sources.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>CURRENT STAGE</label>
                                    <select name="stage" value={newCustomer.stage} onChange={handleInputChange}>
                                        {stages.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>KPI STATUS</label>
                                    <select name="kpiStatus" value={newCustomer.kpiStatus} onChange={handleInputChange}>
                                        {kpiStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* NEXT OF KIN & PAYMENTS */}
                        <div className="form-section">
                            <h4 className="section-title">NEXT OF KIN & PAYMENTS</h4>
                            <div className="form-grid-2">
                                <div className="form-group">
                                    <label>NEXT OF KIN NAME</label>
                                    <input type="text" name="nok_name" placeholder="Contact Person" value={newCustomer.nextOfKin.name} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>NEXT OF KIN RELATIONSHIP</label>
                                    <input type="text" name="nok_relationship" placeholder="e.g. Mother" value={newCustomer.nextOfKin.relationship} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>NEXT OF KIN PHONE</label>
                                    <input type="tel" name="nok_phone" placeholder="+263..." value={newCustomer.nextOfKin.phone} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>INSTALLMENT TYPE</label>
                                    <select name="paymentPlan" value={newCustomer.paymentPlan} onChange={handleInputChange}>
                                        {installmentTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>INITIAL PAYMENT AMOUNT ($)</label>
                                    <input type="number" name="initialPaymentAmount" placeholder="0.00" value={newCustomer.initialPaymentAmount} onChange={handleInputChange} />
                                </div>
                                <div className="form-group">
                                    <label>INITIAL PAYMENT DATE</label>
                                    <input type="date" name="initialPaymentDate" value={newCustomer.initialPaymentDate} onChange={handleInputChange} />
                                </div>
                                <div className="form-group mt-4">
                                    <label>NOTES</label>
                                    <textarea name="notes" placeholder="Additional details..." rows="3" value={newCustomer.notes} onChange={handleInputChange}></textarea>
                                </div>
                                <div className="form-group mt-4 flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="createAccount"
                                        name="createAccount"
                                        checked={newCustomer.createAccount}
                                        onChange={e => setNewCustomer({ ...newCustomer, createAccount: e.target.checked })}
                                        style={{ width: 'auto' }}
                                    />
                                    <label htmlFor="createAccount" style={{ marginBottom: 0 }}>Create Student Portal Account Automatically</label>
                                </div>
                            </div>
                        </div>

                        <div className="form-footer-actions">
                            <button type="button" className="btn btn-secondary" onClick={() => setIsAddMode(false)}>Discard</button>
                            <button type="submit" className="btn btn-primary btn-large">Save Customer Profile</button>
                        </div>
                    </form>
                </div>
            ) : (
                <>
                    <div className="filters-bar">
                        <div className="view-toggle-bar">
                            <button
                                className={`btn-icon ${!isTableView ? 'active' : ''}`}
                                onClick={() => setIsTableView(false)}
                                title="Grid View"
                                style={{ padding: '8px', background: !isTableView ? '#f1f5f9' : 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                <LayoutGrid size={20} color={!isTableView ? 'var(--primary-main)' : '#64748b'} />
                            </button>
                            <button
                                className={`btn-icon ${isTableView ? 'active' : ''}`}
                                onClick={() => setIsTableView(true)}
                                title="Table View"
                                style={{ padding: '8px', background: isTableView ? '#f1f5f9' : 'transparent', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                <List size={20} color={isTableView ? 'var(--primary-main)' : '#64748b'} />
                            </button>
                        </div>
                        <div className="search-input-wrapper">
                            <Search size={18} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search customers..."
                                className="search-input"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="btn btn-secondary flex items-center gap-2">
                            <Filter size={16} /> Filter
                        </button>
                    </div>

                    {isTableView ? (
                        <div className="customers-table-container fade-in">
                            <table className="premium-table">
                                <thead>
                                    <tr>
                                        <th>Customer</th>
                                        <th>Branch</th>
                                        <th>Package</th>
                                        <th style={{ textAlign: 'right' }}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCustomers.map(customer => {
                                        const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown';
                                        return (
                                            <tr key={customer.id}>
                                                <td>
                                                    <div className="table-customer-info">
                                                        <div className="table-avatar">{customerName.charAt(0)}</div>
                                                        <div className="table-name-wrapper">
                                                            <Link to={`/customers/${customer.id}`} className="table-customer-name">{customerName}</Link>
                                                            <span className="table-customer-id">{customer.student_id_number || customer.id}</span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <select
                                                        className="inline-select"
                                                        value={customer.branch_id || ''}
                                                        onChange={(e) => handleQuickUpdate(customer, 'branch', e.target.value)}
                                                        disabled={savingId === `${customer.id}-branch`}
                                                    >
                                                        <option value="">Select Branch</option>
                                                        {branches.map(b => <option key={b.branch_id} value={b.branch_id}>{b.branch_name}</option>)}
                                                    </select>
                                                </td>
                                                <td>
                                                    <select
                                                        className="inline-select"
                                                        value={customer.package_id || ''}
                                                        onChange={(e) => handleQuickUpdate(customer, 'package', e.target.value)}
                                                        disabled={savingId === `${customer.id}-package`}
                                                    >
                                                        <option value="">Select Package</option>
                                                        {packages.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                                                    </select>
                                                </td>
                                                <td>
                                                    <div className="row-actions">
                                                        {savedId?.includes(customer.id) ? (
                                                            <div className="btn-icon-save saved"><Check size={18} /></div>
                                                        ) : (
                                                            <Link to={`/customers/${customer.id}`} className="btn-icon-save"><ChevronRight size={18} /></Link>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="customers-grid">
                            {filteredCustomers.map(customer => {
                                const customerName = customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown';
                                return (
                                    <Link to={`/customers/${customer.id}`} key={customer.id} className="card customer-card">
                                        <div className="customer-header">
                                            <div className="customer-avatar">
                                                {customerName.charAt(0)}
                                            </div>
                                            <div className="customer-info">
                                                <h4 className="customer-name">{customerName}</h4>
                                                <span className="customer-id">{customer.student_id_number || customer.id}</span>
                                            </div>
                                            <div className="icon-btn"><ChevronRight size={20} /></div>
                                        </div>
                                        <div className="customer-details">
                                            <div className="detail-item">
                                                <span className="label">Package</span>
                                                <span className="value">{getPackageName(customer.package_id || customer.packageId)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">Stage</span>
                                                <span className="value text-gold">{customer.stage || 'N/A'}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">Installment</span>
                                                <span className="value">{getInstallmentName(customer.paymentPlan)}</span>
                                            </div>
                                            <div className="detail-item">
                                                <span className="label">Balance</span>
                                                <span className={`value ${customer.balance > 0 ? 'text-red' : 'text-green'}`}>
                                                    ${parseFloat(customer.balance || 0).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                    {hasMoreCustomers && (
                        <div className="flex justify-center mt-6">
                            <button className="btn btn-secondary" onClick={loadMoreCustomers}>
                                Load More ({customers.length} of {totalCustomers})
                            </button>
                        </div>
                    )}
                </>
            )}

            <ImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImport={importCustomers}
                title="Import Customers"
                templateHeaders={customerTemplateHeaders}
                sampleRow={customerSampleRow}
            />
        </div >
    );
};

export default Customers;
